"use client";

import React, { useMemo } from "react";
import styled, { useTheme } from "styled-components";
import { Asset, Item, ImageView, ActivityIndicator, Flex } from "@thenounproject/lingo-core";

import getAssetBackground from "@helpers/getAssetBackground";
import { getAssetPreviewDimensions } from "@helpers/getAssetPreviewDimensions";
import useResponsiveThumbnailSize from "@hooks/useResponsiveThumbnailSize";

const ImageWrapper = styled(Flex).attrs({
  alignItems: "center",
  justifyContent: "center",
})`
  position: relative;
  width: 100%;
  height: 100%;
`;

const AudioOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  pointer-events: auto;
`;

const AudioPlayer = styled.audio`
  display: block;
  width: 100%;
  height: 36px;
`;

const AudioContainer = styled(Flex).attrs({
  position: "relative",
  width: "100%",
  height: "100%",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
})``;

type Props = {
  asset: Asset;
  item?: Item;
  lazy?: boolean;
};

function AssetPreviewAudio({ asset, lazy = true }: Props) {
  const theme = useTheme();
  const { ref, inView, thumbnailSize } = useResponsiveThumbnailSize();
  const backgroundColor = useMemo(() => getAssetBackground(asset, theme), [asset, theme]);
  const hasPadding = asset.meta.backgroundColor !== "clear";

  const dims = useMemo(() => getAssetPreviewDimensions(asset), [asset]);
  const shouldRender = !lazy || inView;
  const hasImage = Boolean(asset.meta?.preview?.id);

  function renderContent() {
    if (!shouldRender || asset.meta.assetProcessing !== "complete") {
      return <ActivityIndicator size="small" color="gray" center />;
    }
    const player = (
      <AudioPlayer src={asset.permalink} controls preload="none" controlsList="nodownload" />
    );
    if (hasImage) {
      return (
        <ImageWrapper>
          <ImageView
            maxHeight={dims.h}
            maxWidth={dims.w}
            width="100%"
            height="100%"
            src={asset.thumbnails?.[thumbnailSize]}
            srcFallback={asset.thumbnailPlaceholders?.[thumbnailSize]}
            icon="content.audio"
            iconSize={32}
          />
          <AudioOverlay>{player}</AudioOverlay>
        </ImageWrapper>
      );
    }
    return player;
  }

  return (
    <AudioContainer
      ref={ref}
      mx="auto"
      aspectRatio={dims?.aspect}
      maxWidth={800}
      p={hasPadding ? "8px" : undefined}
      {...backgroundColor}>
      {renderContent()}
    </AudioContainer>
  );
}

export default React.memo(AssetPreviewAudio);
